<template>
  <div class="flex flex-col gap-y-4">
    <div class="sticky top-0 bg-surface-base pb-2 flex items-center justify-between">
      <StatusFilter
        class="ml-3"
        v-model="statusFilters"
        :options="statusFilterOptions"
        v-if="statusFilterOptions.length !== 0"
      />
      <TextInput v-model="searchQuery" placeholder="Search" inputClasses="h-8" classes="w-1/2" />
    </div>

    <ReservationPodListGroup
      :id="`seating-time-${seatingTime.id}-reservations`"
      v-for="(seatingTime, index) in filteredActiveReservations"
      :key="index"
      :progress="seatingTime.progressCompletion"
    >
      <template #label> {{ seatingTime.timeStr }} </template>
      <ReservationPodItem
        v-for="reservation in seatingTime.reservations"
        :key="reservation.id"
        :reservation="reservation"
        @click="goToReservationDetail(reservation.id)"
      />
    </ReservationPodListGroup>
    <ReservationPodListGroup
      id="cleared-reservations"
      v-if="clearedReservations.length && statusFilters.length === 0"
    >
      <template #label> Cleared Reservations </template>
      <ReservationPodItem
        v-for="reservation in clearedReservations"
        :key="reservation.id"
        :reservation="reservation"
        @click="goToReservationDetail(reservation.id)"
      />
    </ReservationPodListGroup>
    <ReservationPodListGroup
      id="cancelled-reservations"
      v-if="canceledReservations.length && statusFilters.length === 0"
    >
      <template #label> Cancellations </template>
      <ReservationPodItem
        v-for="reservation in canceledReservations"
        :key="reservation.id"
        :reservation="reservation"
        @click="goToReservationDetail(reservation.id)"
      />
    </ReservationPodListGroup>
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import StatusFilter, { StatusOption } from '@/common/components/molecules/StatusFilter.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import ReservationPodItem from '@/modules/reservation/components/molecules/ReservationPodItem.vue'
import ReservationPodListGroup from '@/modules/reservation/components/molecules/ReservationPodListGroup.vue'
import { useSettingStore } from '@/stores/setting'
import { cleanString, sortReservations } from '@/utils/utils'

import {
  PerformanceSeatingTimeRead,
  ReservationId,
  ReservationRead,
  ReservationStatus,
  ReservationStatusType,
} from '@generated/types'

type SeatingTimeGroup = {
  timeStr: string
  progressCompletion: number
  reservations: ReservationRead[]
} & PerformanceSeatingTimeRead

const settingStore = useSettingStore()
const performanceReservationStore = usePerformanceReservationStore()
const { selectedPerformance, performanceReservations } = storeToRefs(performanceReservationStore)
onBeforeMount(() => {
  performanceReservationStore.setSelectedTableIds([])
})

const searchQuery = ref('')
const reservationsBySearchQuery = computed<ReservationRead[]>(() => {
  if (!searchQuery.value) return performanceReservations.value
  return _.filter(performanceReservations.value, r =>
    _.includes(cleanString(r.contact.name), cleanString(searchQuery.value)),
  )
})

const clearedReservations = computed<ReservationRead[]>(function () {
  return _.filter(reservationsBySearchQuery.value, { status: ReservationStatus.CLEARED })
})
const canceledReservations = computed<ReservationRead[]>(function () {
  return _.filter(reservationsBySearchQuery.value, { status: ReservationStatus.CANCELED })
})
const filteredActiveReservations = computed<SeatingTimeGroup[]>(function () {
  if (!selectedPerformance.value) return []
  const reservationsByStatus = _.filter(reservationsBySearchQuery.value, function (reservation) {
    return _.includes(statusFilters.value, reservation.status)
  })
  return groupReservationsBySeatingTime(
    _.reject(
      statusFilters.value.length !== 0 ? reservationsByStatus : reservationsBySearchQuery.value,
      r => r.status === ReservationStatus.CANCELED || r.status === ReservationStatus.CLEARED,
    ),
  )
})
function groupReservationsBySeatingTime(reservations: ReservationRead[]): SeatingTimeGroup[] {
  if (!selectedPerformance.value) return []
  return _.sortBy(
    _.map(selectedPerformance.value.seating_times, function (seatingTime) {
      const groupedReservations = _.filter(reservations, {
        seating_time_id: seatingTime.id,
      }).sort(sortReservations)
      return {
        ...seatingTime,
        timeStr: settingStore.timeStr(seatingTime.time),
        progressCompletion: (groupedReservations.length * 100) / seatingTime.maximum_guests,
        reservations: groupedReservations,
      }
    }),
    ['time'],
  )
}

const STATUS_OPTIONS_MAP: StatusOption[] = [
  {
    value: ReservationStatus.EXPECTED,
    strokeClass: 'stroke-seats-expected',
    selectedFillClass: 'fill-seats-expected/50',
  },
  {
    value: ReservationStatus.ARRIVED,
    strokeClass: 'stroke-seats-arrived',
    selectedFillClass: 'fill-seats-arrived/50',
  },
  {
    value: ReservationStatus.SEATED,
    strokeClass: 'stroke-seats-seated',
    selectedFillClass: 'fill-seats-seated/50',
  },
  {
    value: ReservationStatus.NO_SHOW,
    strokeClass: 'stroke-seats-noShow',
    selectedFillClass: 'fill-seats-noShow/50',
  },
  {
    value: ReservationStatus.UNPAID,
    strokeClass: 'stroke-seats-unpaid',
    selectedFillClass: 'fill-seats-unpaid/50',
  },
]
const statusFilters = ref<ReservationStatusType[]>([])
const statusFilterOptions = computed<StatusOption[]>(function () {
  if (!selectedPerformance.value) return []
  const status = _.uniq(_.map(selectedPerformance.value.reservations, 'status'))
  return _.filter(STATUS_OPTIONS_MAP, function (option) {
    return _.includes(status, option.value)
  })
})

const router = useRouter()
function goToReservationDetail(reservationId: ReservationId) {
  router.push({
    name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
    params: { reservationId: reservationId.toString() },
  })
}
</script>
